import React, {useMemo} from "react";
import {Autocomplete, Box, TextField, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import UIUtils from "../../../../../../utils";

/**
 * Industry selector for industry-scoped import data sources.
 */
const ImportIndustryController = ({
                                      industries = [],
                                      value,
                                      onChange,
                                      column = false,
                                      description,
                                  }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport.dataSourceCatalog'});
    const theme = useTheme();

    const selectedIndustry = useMemo(
        () => industries.find((industry) => String(industry.id) === String(value)) ?? null,
        [industries, value],
    );

    if (industries.length <= 1) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: column ? 'column' : 'row',
                alignItems: column ? 'stretch' : 'center',
                gap: 1.5,
                width: column ? '100%' : 'auto',
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <Autocomplete
                fullWidth
                size="medium"
                options={industries}
                value={selectedIndustry}
                onChange={(_, industry) => onChange(industry?.id ?? null)}
                getOptionLabel={(option) => option?.title ?? ''}
                isOptionEqualToValue={(option, selected) => String(option?.id) === String(selected?.id)}
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {React.cloneElement(
                            UIUtils.getIndustryIcon(option?.iconFileName) ?? <span/>,
                            {style: {width: 20, height: 20}},
                        )}
                        <Typography variant="body2">{option.title}</Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('industryLabel')}
                        placeholder={t('industryPlaceholder')}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: React.cloneElement(
                                UIUtils.getIndustryIcon(selectedIndustry?.iconFileName) ?? <span/>,
                                {style: {width: 24, height: 24, color: theme.vars?.palette.primary.main}}
                            ),
                        }}
                    />
                )}
                sx={{
                    minWidth: {xs: '100%', sm: 280},
                    '& .MuiOutlinedInput-root': {borderRadius: 1},
                }}
            />
            {description && (
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            )}
        </Box>
    );
};

export default ImportIndustryController;
